import type { WhiteLabel } from '@skytech/types'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import useUser, { useProfile } from '@/hooks/user'
import type { UserRole } from '@/types'
import { APP_ROUTES } from '@/util/app-routes'

export const USER_ROLE: Record<string, UserRole> = {
  ADMIN: 'admin',
  CUSTOMER_HEAD_ADMIN: 'customer_head_admin',
  CUSTOMER_ADMIN: 'customer_admin',
  DEPARTMENT_HEAD_CUSTOMER_HEAD: 'department_head_corp',
  DEPARTMENT_HEAD: 'department_head',
  REPORT_USER: 'report_user',
  IT_USER: 'it_user',
  IT_PURCHASER: 'it_purchaser',
}

export const ALL_ROLES = Object.values(USER_ROLE)

export const ADMIN_ROLES = [
  USER_ROLE.ADMIN,
  USER_ROLE.CUSTOMER_HEAD_ADMIN,
  USER_ROLE.CUSTOMER_ADMIN,
]

export const DEPARTMENT_ROLES = [USER_ROLE.DEPARTMENT_HEAD_CUSTOMER_HEAD, USER_ROLE.DEPARTMENT_HEAD]

export const FEATURE_ACTIVATION_ROLES = [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER]

const LEASED_ADMIN_ROLES = [
  USER_ROLE.ADMIN,
  USER_ROLE.CUSTOMER_HEAD_ADMIN,
  USER_ROLE.CUSTOMER_ADMIN,
  USER_ROLE.IT_USER,
]

export const useAccess = (roles: Partial<UserRole>[]) => {
  const navigate = useNavigate()
  const role = useUser()?.role

  if (!role) {
    navigate(APP_ROUTES.HOME)
    return
  }

  !(roles || []).includes(role) && navigate(APP_ROUTES.HOME)
}

export const useAdminAccess = () => useAccess(ADMIN_ROLES)

export const useItPurchaserAccess = () => useAccess([...ADMIN_ROLES, USER_ROLE.IT_PURCHASER])

export const useHasRoleBasedAccess = (roles: Partial<UserRole>[]) => {
  const user = useUser()

  if (!user) return false

  return roles.includes(user.role)
}

export const useHasFeatureActivationAccess = () => {
  const user = useUser()

  if (!user) return false

  return FEATURE_ACTIVATION_ROLES.includes(user.role)
}

export const useIsAdmin = () => {
  const user = useUser()

  if (!user) return false

  return ADMIN_ROLES.includes(user.role)
}

export const useIsItUser = () => {
  const user = useUser()
  if (!user) return false
  return user.role === USER_ROLE.IT_USER
}

export const useIsItPurchaser = () => {
  const user = useUser()
  if (!user) return false
  return user.role === USER_ROLE.IT_PURCHASER
}

export const useIsDepartmentHeadUser = () => {
  const user = useUser()
  if (!user) return false
  return DEPARTMENT_ROLES.includes(user.role)
}

export const useCanManageOnOffboarding = () => {
  const user = useUser()
  const profile = useProfile()

  if (!user) return false

  if (!DEPARTMENT_ROLES.includes(user.role)) {
    return true
  }

  if (!profile) return false

  return profile.featureAccess.allowDepartmentHeadOnboardingManagement !== false
}

export const useHasGlobalAccess = () => {
  const user = useUser()

  if (!user) return false

  return [USER_ROLE.ADMIN, USER_ROLE.CUSTOMER_HEAD_ADMIN].includes(user.role)
}

export const useHasAssetEditingAccess = () => {
  const user = useUser()

  if (!user) return false

  const allowedRoles = [...ADMIN_ROLES, USER_ROLE.IT_USER]

  return allowedRoles.includes(user.role)
}

export const useLeasedFeatureAccess = (role: Partial<UserRole>) => LEASED_ADMIN_ROLES.includes(role)

export const useWhiteLabelBasedAccess = (whiteLabelList: WhiteLabel[]) => {
  const navigate = useNavigate()
  const { whiteLabel: currentWhiteLabel } = useAppContext()

  if (whiteLabelList.includes(currentWhiteLabel)) {
    navigate(APP_ROUTES.HOME)
    return
  }
}

export const useIsSkytechWL = () => {
  const { whiteLabel } = useAppContext()
  if (!whiteLabel) return false

  const skytech = ['skytech', 'control', 'tcm', 'ikomm']
  return skytech.includes(whiteLabel)
}

export const useIsThreeStepIT = () => {
  const { whiteLabel } = useAppContext()

  if (!whiteLabel) return false

  return whiteLabel === 'threeStepIT'
}

export const useIsTelia = () => {
  const { whiteLabel } = useAppContext()

  if (!whiteLabel) return false

  return whiteLabel === 'telia'
}

export const hasUserGuideAccessByWl = () => {
  const { whiteLabel } = useAppContext()
  if (!whiteLabel) return false

  const userGuideWlList: Array<WhiteLabel> = [
    'skytech',
    'control',
    'tcm',
    'threeStepIT',
    'telia',
    'mobit',
    'elkjop',
    'komplett',
    'evry',
    'ikomm',
  ]
  return userGuideWlList.includes(whiteLabel)
}
